/**
 * REX-108 — tiny TTL cache for short-lived API responses (Feb 2026)
 * Used by RevenueSnapshot + RevenuePage so tab-switching, period-changing
 * and modal-closing don't re-fetch the same data within the TTL window.
 *
 * Note: in-memory only (page-local). Cleared on hard reload.
 */
const _store = new Map(); // key → { value, expiresAt }

export function ttlCacheGet(key) {
  const hit = _store.get(key);
  if (!hit) return undefined;
  if (Date.now() > hit.expiresAt) {
    _store.delete(key);
    return undefined;
  }
  return hit.value;
}

export function ttlCacheSet(key, value, ttlMs = 30_000) {
  _store.set(key, { value, expiresAt: Date.now() + ttlMs });
  return value;
}

export function ttlCacheClear(prefix) {
  if (!prefix) {
    _store.clear();
    return;
  }
  for (const k of Array.from(_store.keys())) {
    if (k.startsWith(prefix)) _store.delete(k);
  }
}

/**
 * Memoised fetch — returns cached JSON for ttlMs after first successful call.
 * Falls through to live network on cache miss / expiry.
 */
export async function cachedJson(url, options = {}, ttlMs = 30_000) {
  const key = `GET:${url}`;
  const hit = ttlCacheGet(key);
  if (hit !== undefined) return hit;
  const r = await fetch(url, { credentials: "include", ...options });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = await r.json();
  ttlCacheSet(key, data, ttlMs);
  return data;
}
